import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import { Crew, Rank, Company, CompanyAdmin, Vessel } from '../core/_models'
import {
  CrewTraining,
  CrewTrainingMode,
  CrewTrainingType,
  CrewTrainingWorkflowStatus,
  CreateCrewTrainingRequest,
  UpdateCrewTrainingRequest,
  createCrewTraining,
  deleteCrewTraining,
  getCompanyAdminList,
  getCompanyList,
  getCrewList,
  getCrewTrainings,
  getRanksforList,
  rejectCrewTraining,
  reviewCrewTraining,
  submitCrewTraining,
  updateCrewTraining,
  uploadCrewTrainingAttachment,
} from '../core/_requests'
import CrewTrainingModal, { PendingTrainingAttachment } from '../components/CrewTrainingModal'
import ViewCrewTrainingModal from '../components/ViewCrewTrainingModal'
import CrewTrainingReviewModal from '../components/CrewTrainingReviewModal'
import CrewTrainingAttachmentsModal from '../components/CrewTrainingAttachmentsModal'
import { FileViewerModal } from '../../QHSE/components/FileViewerModal'
import { RemarksCell, RemarksModal } from '../../QHSE/components/StructuredRemarksView'
import {
  CREWING_ACTION_PERMISSIONS,
  CREWING_READ_PERMISSIONS,
  useCrewingCanRead,
  useCrewingPermissionRevision,
} from '../core/crewingPermissionAccess'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import {
  applyCrewingPresentationFilters,
  allowCompanyPresentationFilters,
  extractRows,
} from '../core/crewingPresentationFilters'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  resolveVesselCompanyAdminId,
  resolveVesselCompanyGroupId,
} from '../../../shared/access/tenantScopeMatch'

type SortKey = keyof CrewTraining | 'srNo'
type SortConfig = { key: SortKey; direction: 'asc' | 'desc' }

const TRAINING_TYPE_LABEL: Record<CrewTrainingType, string> = {
  PRE_JOINING_VALUE_ADDED: 'Pre Joining Value Added',
  PRE_JOINING_SHIP_SPECIFIC: 'Pre Joining Ship Specific',
  COMPANY_SPECIFIC: 'Company Specific',
  ONBOARD_TRAINING: 'Onboard Training',
  ON_LEAVE_TRAINING: 'On Leave Training',
  OTHER_VALUE_ADDED: 'Other Value Added',
}

const MODE_LABEL: Record<CrewTrainingMode, string> = {
  REMOTE: 'Remote',
  PHYSICAL: 'Physical',
  VIDEO: 'Video',
}

const MODE_BADGE_CLASS: Record<CrewTrainingMode, string> = {
  REMOTE: 'badge-light-primary',
  PHYSICAL: 'badge-light-success',
  VIDEO: 'badge-light-info',
}

const DEFAULT_TRAINING_TYPE: CrewTrainingType = 'PRE_JOINING_VALUE_ADDED'
const DEFAULT_MODE: CrewTrainingMode = 'REMOTE'

const CrewTrainingPage: FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth()
  const {
    isCrew,
    isOfficeRole,
    isCompanyGroupAdmin,
    isCompanyAdmin: baseIsCompanyAdmin,
    isOperator,
  } = useCrewingRoleAccess()
  const roleId = currentUser?.role?.id

  const operatorCompanyAdminId = Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? 0) || undefined
  const isCompanyAdmin = baseIsCompanyAdmin || (isOperator && !!operatorCompanyAdminId)
  const crewRankName = ((currentUser as any)?.rank?.rank ?? '').toLowerCase()
  const crewRankId = Number((currentUser as any)?.rank?.id ?? 0)
  const isCrewMaster = isCrew && (crewRankId === 1 || crewRankName.includes('master'))
  const currentCrewId = isCrew ? Number((currentUser as any)?.roleEntityId ?? 0) : 0
  const roleEntityId = Number((currentUser as any)?.roleEntityId ?? 0)
  const currentCrewUsername = (currentUser as any)?.username ?? ''
  const currentCrewName = (currentUser as any)?.roleEntityName ?? currentCrewUsername

  const showCompanyFilter = isSuperAdmin
  const showSubCompanyFilter = isSuperAdmin || isCompanyGroupAdmin
  const showVesselFilter = isSuperAdmin || isCompanyGroupAdmin || isCompanyAdmin || isOperator
  const canCreate = isSuperAdmin || hasPermission('CREATE_CREW_TRAINING')
  const canReview = isSuperAdmin || (hasPermission('APPROVE_CREW_TRAINING') && isOfficeRole)
  const canSubmitTraining = isSuperAdmin || hasPermission('APPROVE_CREW_TRAINING')
  const canDelete = isSuperAdmin || hasPermission('DELETE_CREW_TRAINING')
  const useCrewDropdownFilter = isCompanyGroupAdmin || isCompanyAdmin

  const fixedCompanyGroupId = useMemo(() => {
    if (isCompanyGroupAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? (roleEntityId || 0)) || undefined
    }
    if (isCompanyAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyAdmin?.cga?.id ?? 0) || undefined
    }
    return undefined
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, roleEntityId])

  const fixedCompanyAdminId = useMemo(() => {
    if (!isCompanyAdmin) return undefined
    return Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? (roleEntityId || 0)) || undefined
  }, [currentUser, isCompanyAdmin, roleEntityId])

  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, roleId, isOperator)

  const canReadTraining = useCrewingCanRead(CREWING_READ_PERMISSIONS.TRAINING)
  const permissionRevision = useCrewingPermissionRevision()

  const [trainings, setTrainings] = useState<CrewTraining[]>([])
  const [ranks, setRanks] = useState<Rank[]>([])
  const [crewMembers, setCrewMembers] = useState<Crew[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])

  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  useEffect(() => {
    if (globalState.vessel?.id) {
      setSelectedVesselId(Number(globalState.vessel.id))
    } else if (isCrew && currentUser?.vessel?.id) {
      setSelectedVesselId(Number(currentUser.vessel.id))
    } else {
      setSelectedVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setSelectedCompanyGroupId(Number(globalState.company.id))
        setSelectedCompanyAdminId('')
      } else {
        setSelectedCompanyAdminId(Number(globalState.company.id))
        setSelectedCompanyGroupId('')
      }
    } else {
      setSelectedCompanyGroupId(isCompanyGroupAdmin ? (fixedCompanyGroupId ?? '') : '')
      setSelectedCompanyAdminId(isCompanyAdmin ? (fixedCompanyAdminId ?? '') : '')
    }
  }, [
    globalState.vessel,
    globalState.company,
    isCrew,
    currentUser,
    isCompanyGroupAdmin,
    isCompanyAdmin,
    fixedCompanyGroupId,
    fixedCompanyAdminId,
  ])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<'All' | CrewTrainingWorkflowStatus>('All')
  const [typeFilter, setTypeFilter] = useState<'All' | CrewTrainingType>('All')
  const [modeFilter, setModeFilter] = useState<'All' | CrewTrainingMode>('All')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'crewName', direction: 'asc' })

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNewTraining, setIsNewTraining] = useState(false)
  const [editingTraining, setEditingTraining] = useState<CrewTraining | null>(null)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewTraining, setViewTraining] = useState<CrewTraining | null>(null)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [reviewingTraining, setReviewingTraining] = useState<CrewTraining | null>(null)
  const [isReviewSaving, setIsReviewSaving] = useState(false)
  const [attachmentsModal, setAttachmentsModal] = useState<{ visible: boolean; training: CrewTraining | null }>({ visible: false, training: null })
  const [fileModal, setFileModal] = useState<{ visible: boolean; title: string; fileName?: string; viewUrl?: string; downloadUrl?: string }>({ visible: false, title: '' })
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const didInitFiltersRef = useRef(false)

  const fetchTrainings = async () => {
    setIsLoading(true)
    try {
      setTrainings(canReadTraining ? extractRows<CrewTraining>(await getCrewTrainings()) : [])
    } catch (error) {
      console.error(error)
      showErrorToast(error, 'Failed to fetch training records', 'load')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!roleId || !canReadTraining) return
    fetchTrainings()
    getRanksforList().then(setRanks).catch(console.error)
    getCrewList().then(setCrewMembers).catch(console.error)
    if (showSubCompanyFilter) getCompanyList().then(setCompanies).catch(console.error)
    if (showCompanyFilter) getCompanyAdminList().then(setCompanyAdmins).catch(console.error)
    if (showVesselFilter) fetchAccessibleVesselsForCurrentUser().then((list) => setVessels(normalizeAccessibleVessels(list))).catch(console.error)
  }, [roleId, canReadTraining, permissionRevision])

  useEffect(() => {
    if (didInitFiltersRef.current) return
    if (isCompanyGroupAdmin) {
      const cgaId = currentUser?.companyGroupAdminId ?? currentUser?.companyGroupAdmin?.id
      if (cgaId) setSelectedCompanyGroupId(cgaId)
    }
    if (isCompanyAdmin) {
      const caId = currentUser?.companyAdminId ?? currentUser?.companyAdmin?.id
      if (caId) setSelectedCompanyAdminId(caId)
    }
    if (isCrew) {
      const vesselId = currentUser?.vessel?.id
      if (vesselId) setSelectedVesselId(vesselId)
    }
    didInitFiltersRef.current = true
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, isCrew])

useEffect(() => {
  setCurrentPage(1)
}, [searchTerm, selectedCrewId, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId, statusFilter, typeFilter, modeFilter])

const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
  resolveVesselCompanyAdminId(v) ?? v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined
const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
  resolveVesselCompanyGroupId(v) ?? (v as any).companyGroupAdminId ?? v.companyGroupAdmin?.id ?? (v.companyAdmin as any)?.cgaid?.id ?? v.companyAdmin?.cga?.id

const effectiveVessels = useMemo(() => {
  const map = new Map<number, Vessel>()
  ;(globalVessels || []).forEach((v) => {
    if (v?.id) map.set(Number(v.id), v)
  })
  ;(vessels || []).forEach((v) => {
    if (v?.id) map.set(Number(v.id), v)
  })
  return Array.from(map.values())
}, [globalVessels, vessels])

const vesselById = useMemo(() => new Map(effectiveVessels.map((v) => [v.id, v])), [effectiveVessels])
const selfCrewMember = useMemo(() => {
  if (!isCrew) return undefined
  if (currentCrewId) {
    const byId = crewMembers.find((member) => member.id === currentCrewId)
    if (byId) return byId
  }
  if (currentCrewUsername) {
    return crewMembers.find((member) => member.username === currentCrewUsername)
  }
  return undefined
}, [isCrew, currentCrewId, currentCrewUsername, crewMembers])

const selectableCrewMembers = useMemo(() => {
  if (!isCrew || isCrewMaster) return crewMembers
  return selfCrewMember ? [selfCrewMember] : []
}, [isCrew, isCrewMaster, crewMembers, selfCrewMember])



const crewFilterOptions = useMemo(() => {
  const uniqueById = new Map<number, string>()

  crewMembers.forEach((crew) => {
    const crewId = Number(crew.id)
    if (!Number.isFinite(crewId) || crewId <= 0) return

    const crewVesselId = crew.vesselId ?? crew.vessel?.id
    const crewVessel = crewVesselId ? vesselById.get(crewVesselId) : undefined
    const crewCompanyGroupId =
      (crew as any)?.companyGroupAdminId ??
      (crew as any)?.companyGroupAdmin?.id ??
      (crewVessel ? getVesselCompanyGroupId(crewVessel) : undefined)
    const crewCompanyAdminId =
      (crew as any)?.companyAdminId ??
      (crew as any)?.companyAdmin?.id ??
      (crewVessel ? getVesselCompanyAdminId(crewVessel) : undefined)

    const matchesCompanyGroup = fixedCompanyGroupId != null
      ? crewCompanyGroupId === fixedCompanyGroupId
      : !selectedCompanyGroupId || crewCompanyGroupId === Number(selectedCompanyGroupId)
    const matchesCompanyAdmin = fixedCompanyAdminId != null
      ? crewCompanyAdminId === fixedCompanyAdminId
      : !selectedCompanyAdminId || crewCompanyAdminId === Number(selectedCompanyAdminId)
    const matchesVessel = !selectedVesselId || crewVesselId === Number(selectedVesselId)

    if (!matchesCompanyGroup || !matchesCompanyAdmin || !matchesVessel) return
    uniqueById.set(crewId, crew.name || `Crew ${crewId}`)
  })

  if (uniqueById.size === 0) {
    trainings.forEach((training) => {
      const trainingVessel = training.vesselId ? vesselById.get(training.vesselId) : undefined
      const trainingCompanyGroupId = trainingVessel ? getVesselCompanyGroupId(trainingVessel) : undefined
      const trainingCompanyAdminId = trainingVessel ? getVesselCompanyAdminId(trainingVessel) : undefined
      const matchesCompanyGroup = fixedCompanyGroupId != null
        ? trainingCompanyGroupId === fixedCompanyGroupId
        : !selectedCompanyGroupId || trainingCompanyGroupId === Number(selectedCompanyGroupId)
      const matchesCompanyAdmin = fixedCompanyAdminId != null
        ? trainingCompanyAdminId === fixedCompanyAdminId
        : !selectedCompanyAdminId || trainingCompanyAdminId === Number(selectedCompanyAdminId)
      const matchesVessel = !selectedVesselId || training.vesselId === Number(selectedVesselId)

      if (!matchesCompanyGroup || !matchesCompanyAdmin || !matchesVessel) return
      if (!uniqueById.has(training.crewId)) {
        uniqueById.set(training.crewId, training.crewName || `Crew ${training.crewId}`)
      }
    })
  }

  return Array.from(uniqueById.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}, [crewMembers, trainings, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId, vesselById, fixedCompanyGroupId, fixedCompanyAdminId])

useEffect(() => {
  if (!selectedCrewId) return
  const exists = crewFilterOptions.some((crew) => crew.id === Number(selectedCrewId))
  if (!exists) setSelectedCrewId('')
}, [selectedCrewId, crewFilterOptions])

const filteredTrainings = useMemo(() => {
  if (!canReadTraining) return []

  return applyCrewingPresentationFilters(trainings, {
    searchTerm: useCrewDropdownFilter ? undefined : searchTerm,
    selectedCrewId: useCrewDropdownFilter ? selectedCrewId : undefined,
    selectedVesselId,
    allowCompanyPresentationFilters: allowCompanyFilters,
    selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
    selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
  }, {
    vesselById,
    extraPredicate: (training) => {
      const typed = training
      const term = searchTerm.trim().toLowerCase()
      const matchesCrew = !selectedCrewId || typed.crewId === Number(selectedCrewId)
      const matchesSearch =
        !term ||
        (typed.crewName || '').toLowerCase().includes(term) ||
        (typed.rankName || '').toLowerCase().includes(term) ||
        (typed.vesselName || '').toLowerCase().includes(term) ||
        (typed.topic || '').toLowerCase().includes(term) ||
        (typed.trainingType || '').toLowerCase().includes(term) ||
        (typed.mode || '').toLowerCase().includes(term)
      const matchesStatus = statusFilter === 'All' || typed.workflowStatus === statusFilter
      const matchesType = typeFilter === 'All' || typed.trainingType === typeFilter
      const matchesMode = modeFilter === 'All' || typed.mode === modeFilter
      return matchesCrew && matchesSearch && matchesStatus && matchesType && matchesMode
    },
  })
}, [
  trainings,
  canReadTraining,
  searchTerm,
  selectedCrewId,
  statusFilter,
  typeFilter,
  modeFilter,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  vesselById,
  allowCompanyFilters,
  useCrewDropdownFilter,
])

const sortedTrainings = useMemo(() => {
  const rows = [...filteredTrainings]
  rows.sort((a, b) => {
    const first = a[sortConfig.key as keyof CrewTraining]
    const second = b[sortConfig.key as keyof CrewTraining]
    if (typeof first === 'number' && typeof second === 'number') return sortConfig.direction === 'asc' ? first - second : second - first
    return sortConfig.direction === 'asc' ? String(first || '').localeCompare(String(second || '')) : String(second || '').localeCompare(String(first || ''))
  })
  return rows
}, [filteredTrainings, sortConfig])

const indexOfLast = currentPage * rowsPerPage
const indexOfFirst = indexOfLast - rowsPerPage
const currentRows = sortedTrainings.slice(indexOfFirst, indexOfLast)
const totalPages = Math.ceil(sortedTrainings.length / rowsPerPage)

const totals = useMemo(
  () => ({
    total: filteredTrainings.length,
    submitted: filteredTrainings.filter((t) => t.workflowStatus === 'SUBMITTED').length,
    reviewed: filteredTrainings.filter((t) => t.workflowStatus === 'REVIEWED').length,
    rejected: filteredTrainings.filter((t) => t.workflowStatus === 'REJECTED').length,
  }),
  [filteredTrainings]
)

const handleSort = (key: SortKey) => {
  setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }))
}

const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
  const container = event.currentTarget
  if (container.scrollWidth <= container.clientWidth) return
  if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return

  const canScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight
  const canScrollUp = container.scrollTop > 0
  if ((event.deltaY > 0 && canScrollDown) || (event.deltaY < 0 && canScrollUp)) return

  event.preventDefault()
  container.scrollLeft += event.deltaY
}

const canEditTraining = (training: CrewTraining) =>
  (isSuperAdmin || hasPermission('UPDATE_CREW_TRAINING')) && training.workflowStatus === 'DRAFT'

const withResolvedDisplayFields = (training: CrewTraining): CrewTraining => {
  const crew = crewMembers.find((c) => c.id === training.crewId)
  const crewAny = crew as any
  const resolvedRankId = training.rankId ?? crew?.rankId ?? crewAny?.rank?.id ?? null
  const resolvedRankNameFromRanks =
    resolvedRankId != null ? ranks.find((r) => r.id === resolvedRankId)?.rank : undefined
  const resolvedRankName =
    training.rankName ??
    resolvedRankNameFromRanks ??
    crewAny?.rank?.rank ??
    null

  return {
    ...training,
    crewName: training.crewName || crew?.name || '-',
    rankId: resolvedRankId,
    rankName: resolvedRankName,
    trainingType: (training.trainingType || DEFAULT_TRAINING_TYPE) as CrewTrainingType,
    mode: (training.mode || DEFAULT_MODE) as CrewTrainingMode,
    topic: training.topic ?? '',
    remarks: training.remarks ?? '',
  }
}

const handleNewTraining = () => {
  const defaultCrew = !isCrew || isCrewMaster ? null : selfCrewMember ?? null
  const defaultCrewName = defaultCrew?.name ?? (isCrew && !isCrewMaster ? currentCrewName : '')
  const defaultRankId = defaultCrew?.rankId ?? (isCrew && !isCrewMaster ? (crewRankId || null) : null)
  const defaultRankName =
    (defaultRankId ? ranks.find((rank) => rank.id === defaultRankId)?.rank : undefined) ??
    (isCrew && !isCrewMaster ? (currentUser as any)?.rank?.rank : undefined) ??
    null

  setEditingTraining({
    id: 0,
    srNo: trainings.length + 1,
    crewId: defaultCrew?.id ?? (isCrew && !isCrewMaster ? currentCrewId : 0),
    crewName: defaultCrewName,
    vesselId: defaultCrew?.vessel?.id ?? defaultCrew?.vesselId ?? (isCrew && !isCrewMaster ? currentUser?.vessel?.id ?? null : null),
    vesselName: defaultCrew?.vessel?.fleet_name ?? (isCrew && !isCrewMaster ? currentUser?.vessel?.fleet_name ?? null : null),
    rankId: defaultRankId,
    rankName: defaultRankName,
    trainingType: DEFAULT_TRAINING_TYPE,
    topic: '',
    mode: DEFAULT_MODE,
    remarks: '',
    officeRemarks: null,
    workflowStatus: 'DRAFT',
    submittedAt: null,
    reviewedAt: null,
    rejectedAt: null,
    reviewedByName: null,
    rejectionReason: null,
    attachments: [],
    attachmentCount: 0,
    createdAt: '',
    updatedAt: '',
  })
  setIsNewTraining(true)
  setIsModalOpen(true)
}

const handleSaveTraining = async (pendingFiles: PendingTrainingAttachment[]) => {
  if (!editingTraining) return
  if (!editingTraining.crewId) {
    toast.error('Crew member is required')
    return
  }
  if (!editingTraining.trainingType) {
    toast.error('Training type is required')
    return
  }
  if (!editingTraining.mode) {
    toast.error('Training mode is required')
    return
  }
  if (!editingTraining.topic?.trim()) {
    toast.error('Training topic is required')
    return
  }

  setIsSaving(true)
  try {
    if (isNewTraining) {
      const payload: CreateCrewTrainingRequest = {
        crewId: editingTraining.crewId,
        vesselId: editingTraining.vesselId || undefined,
        rankId: editingTraining.rankId || undefined,
        trainingType: editingTraining.trainingType,
        topic: editingTraining.topic.trim(),
        mode: editingTraining.mode,
        remarks: editingTraining.remarks || '',
      }
      const created = await createCrewTraining(payload)
      for (const file of pendingFiles || []) {
        await uploadCrewTrainingAttachment(created.id, file)
      }
      toast.success('Training record created successfully')
    } else {
      const payload: UpdateCrewTrainingRequest = {
        rankId: editingTraining.rankId || undefined,
        trainingType: editingTraining.trainingType,
        topic: editingTraining.topic.trim(),
        mode: editingTraining.mode,
        remarks: editingTraining.remarks || '',
      }
      await updateCrewTraining(editingTraining.id, payload)
      toast.success('Training record updated successfully')
    }
    setIsModalOpen(false)
    setEditingTraining(null)
    setIsNewTraining(false)
    await fetchTrainings()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to save training record', isNewTraining ? 'create' : 'update')
  } finally {
    setIsSaving(false)
  }
}

const handleSubmit = async (training: CrewTraining) => {
  if (!canSubmitTraining) {
    toast.error('You do not have permission to submit training records')
    return
  }
  try {
    await submitCrewTraining(training.id)
    toast.success('Training submitted for review')
    await fetchTrainings()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to submit training', 'save')
  }
}

const deleteTrainingNow = async (training: CrewTraining) => {
  try {
    await deleteCrewTraining(training.id)
    toast.success('Draft training deleted')
    await fetchTrainings()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to delete training', 'delete')
  }
}

const handleDeleteDraft = (training: CrewTraining) => {
  const toastId = `delete-training-${training.id}`
  if (toast.isActive(toastId)) return
  toast.info(
    ({ closeToast }) => (
      <div>
        <div className='mb-2'>Delete draft training record for {training.crewName}?</div>
        <div className='d-flex gap-2'>
          <button
            className='btn btn-sm btn-danger'
            onClick={async () => {
              closeToast?.()
              await deleteTrainingNow(training)
            }}
          >
            Delete
          </button>
          <button className='btn btn-sm btn-light' onClick={() => closeToast?.()}>
            Cancel
          </button>
        </div>
      </div>
    ),
    {
      toastId,
      autoClose: false,
      closeOnClick: false,
    }
  )
}

const handleSaveReviewOnly = async (officeRemarks: string) => {
  if (!reviewingTraining) return
  setIsReviewSaving(true)
  try {
    await updateCrewTraining(reviewingTraining.id, { officeRemarks: officeRemarks.trim() })
    toast.success('Review remarks saved')
    setIsReviewModalOpen(false)
    setReviewingTraining(null)
    await fetchTrainings()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to save review remarks', 'save')
  } finally {
    setIsReviewSaving(false)
  }
}

const handleMarkReviewed = async (officeRemarks: string) => {
  if (!reviewingTraining) return
  setIsReviewSaving(true)
  try {
    await updateCrewTraining(reviewingTraining.id, { officeRemarks: officeRemarks.trim() })
    await reviewCrewTraining(reviewingTraining.id)
    toast.success('Training reviewed successfully')
    setIsReviewModalOpen(false)
    setReviewingTraining(null)
    await fetchTrainings()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to review training', 'save')
  } finally {
    setIsReviewSaving(false)
  }
}

const handleReject = async ({ officeRemarks, reason }: { officeRemarks: string; reason: string }) => {
  if (!reviewingTraining) return
  if (!reason.trim()) {
    toast.error('Rejection reason is required')
    return
  }
  setIsReviewSaving(true)
  try {
    if (officeRemarks.trim()) {
      await updateCrewTraining(reviewingTraining.id, { officeRemarks: officeRemarks.trim() })
    }
    await rejectCrewTraining(reviewingTraining.id, reason.trim())
    toast.success('Training rejected')
    setIsReviewModalOpen(false)
    setReviewingTraining(null)
    await fetchTrainings()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to reject training', 'save')
  } finally {
    setIsReviewSaving(false)
  }
}

const handleUploadExistingAttachment = async (files: FileList | null) => {
  if (!editingTraining || !editingTraining.id || !files || files.length === 0) return
  try {
    let latest: CrewTraining | null = null
    for (const file of Array.from(files)) {
      latest = await uploadCrewTrainingAttachment(editingTraining.id, file)
    }
    if (latest) setEditingTraining(latest)
    await fetchTrainings()
    toast.success('Attachment uploaded successfully')
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to upload attachment', 'update')
  }
}

const renderStatusBadge = (status: CrewTrainingWorkflowStatus) => {
  if (status === 'DRAFT') return <span className='badge badge-light-secondary'>Draft</span>
  if (status === 'SUBMITTED') return <span className='badge badge-light-warning'>Submitted</span>
  if (status === 'REVIEWED') return <span className='badge badge-light-success'>Reviewed</span>
  return <span className='badge badge-light-danger'>Rejected</span>
}

const renderActions = (training: CrewTraining) => (
  <div className='d-flex align-items-center gap-1'>
    <button className='btn btn-icon btn-sm' title='View' onClick={() => { setViewTraining(withResolvedDisplayFields(training)); setIsViewModalOpen(true) }}>
      <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
    </button>

    {canEditTraining(training) && (
      <button className='btn btn-icon btn-sm' title='Edit' onClick={() => { setEditingTraining(withResolvedDisplayFields(training)); setIsNewTraining(false); setIsModalOpen(true) }}>
        <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
      </button>
    )}

    {canDelete && training.workflowStatus === 'DRAFT' && (
      <button className='btn btn-icon btn-sm' title='Delete' onClick={() => handleDeleteDraft(training)}>
        <KTSVG path='/media/map/red-cross-icon.svg' className='svg-icon-3' />
      </button>
    )}

    {canSubmitTraining && training.workflowStatus === 'DRAFT' && (
      <button type='button' className='btn btn-light-primary btn-sm' onClick={() => handleSubmit(training)}>Submit</button>
    )}

    {canReview && training.workflowStatus === 'SUBMITTED' && (
      <button type='button' className='btn btn-light-warning btn-sm' onClick={() => { setReviewingTraining(training); setIsReviewModalOpen(true) }}>Review</button>
    )}
  </div>
)

return (
  <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
    <div className='d-flex flex-column flex-column-fluid'>
      <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
        <div className='card'>
          <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
            <div>
              <h3 className='card-label text-dark fw-bold'>Crew Training</h3>
              <p className='text-muted mb-0 mt-1'>Track crew training records with workflow, review remarks, and attachments.</p>
            </div>
            <div className='card-toolbar'>
              {canCreate && (
                <button
                  type='button'
                  className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(29, 78, 216)',
                    borderColor: 'rgb(29, 78, 216)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={handleNewTraining}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <line x1='12' y1='5' x2='12' y2='19'></line>
                    <line x1='5' y1='12' x2='19' y2='12'></line>
                  </svg>
                  Add Training
                </button>
              )}
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='row gx-3 gy-3'>
              <div className='col-md-3'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>{useCrewDropdownFilter ? 'Crew' : 'Search'}</label>
                {useCrewDropdownFilter ? (
                  <select className='form-select' value={selectedCrewId} onChange={(e) => setSelectedCrewId(e.target.value ? Number(e.target.value) : '')}>
                    <option value=''>All Crew</option>
                    {crewFilterOptions.map((crew) => (
                      <option key={crew.id} value={crew.id}>{crew.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Search crew or rank'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                )}
              </div>

              <div className='col-md-2'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>Status</label>
                <select className='form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'All' | CrewTrainingWorkflowStatus)}>
                  <option value='All'>All Status</option>
                  {(isSuperAdmin || !isOfficeRole) && <option value='DRAFT'>Draft</option>}
                  <option value='SUBMITTED'>Submitted</option>
                  <option value='REVIEWED'>Reviewed</option>
                  <option value='REJECTED'>Rejected</option>
                </select>
              </div>

              <div className='col-md-4'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>Training Type</label>
                <select className='form-select' value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as 'All' | CrewTrainingType)}>
                  <option value='All'>All Training Types</option>
                  {Object.entries(TRAINING_TYPE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className='col-md-3'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>Mode</label>
                <select className='form-select' value={modeFilter} onChange={(e) => setModeFilter(e.target.value as 'All' | CrewTrainingMode)}>
                  <option value='All'>All Modes</option>
                  {Object.entries(MODE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='row g-3'>
              <div className='col-md-3'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>Total Trainings</div><div className='fs-3 fw-bold text-dark'>{totals.total}</div></div></div>
              <div className='col-md-3'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>Submitted</div><div className='fs-3 fw-bold text-warning'>{totals.submitted}</div></div></div>
              <div className='col-md-3'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>Reviewed</div><div className='fs-3 fw-bold text-success'>{totals.reviewed}</div></div></div>
              <div className='col-md-3'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>Rejected</div><div className='fs-3 fw-bold text-danger'>{totals.rejected}</div></div></div>
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='report-table'>
              <div className='table-responsive' onWheel={handleTableHorizontalWheelScroll}>
                <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th className='text-center text-nowrap' style={{ minWidth: '70px' }}>Sr/No</th>
                        <th style={{ minWidth: '180px' }}><div className='d-flex align-items-center text-nowrap'><span className='me-1'>Name</span><button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('crewName')}><KTSVG path={`/media/map/sort-col-${sortConfig.key === 'crewName' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} className='svg-icon-3' /></button></div></th>
                        <th style={{ minWidth: '140px' }}>Rank</th>
                        <th style={{ minWidth: '220px' }}>Training Type</th>
                        <th style={{ minWidth: '220px' }}>Topic</th>
                        <th style={{ minWidth: '130px' }}>Mode</th>
                        <th style={{ minWidth: '140px' }}>Attachments</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }}>Remarks</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }}>Office Remarks</th>
                        <th style={{ minWidth: '120px' }}>Status</th>
                        <th style={{ minWidth: '230px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <TableSkeleton rows={rowsPerPage || 10} cols={11} />
                      ) : currentRows.length === 0 ? (
                        <tr><td colSpan={11} className='text-center text-muted py-4'>No crew training data found.</td></tr>
                      ) : (
                        currentRows.map((row, idx) => {
                          const resolvedRow = withResolvedDisplayFields(row)
                          return (
                            <tr key={resolvedRow.id || idx}>
                              <td className='text-center'>{indexOfFirst + idx + 1}</td>
                              <td>{resolvedRow.crewName || '-'}</td>
                              <td>{resolvedRow.rankName || '-'}</td>
                              <td>{TRAINING_TYPE_LABEL[resolvedRow.trainingType] || resolvedRow.trainingType}</td>
                              <td title={resolvedRow.topic || '-'}>
                                <div className='text-truncate' style={{ maxWidth: 220 }}>{resolvedRow.topic || '-'}</div>
                              </td>
                              <td><span className={`badge ${MODE_BADGE_CLASS[resolvedRow.mode] || 'badge-light'}`}>{MODE_LABEL[resolvedRow.mode] || resolvedRow.mode}</span></td>
                              <td>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary text-nowrap'
                                  style={{ whiteSpace: 'nowrap' }}
                                  onClick={() => setAttachmentsModal({ visible: true, training: resolvedRow })}
                                >
                                  View Files ({resolvedRow.attachmentCount || resolvedRow.attachments?.length || 0})
                                </button>
                              </td>
                              <td>
                                <RemarksCell
                                  text={resolvedRow.remarks}
                                  title='Training Remarks'
                                  subtitle={resolvedRow.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td>
                                <RemarksCell
                                  text={resolvedRow.workflowStatus === 'REJECTED' && resolvedRow.rejectionReason ? resolvedRow.rejectionReason : resolvedRow.officeRemarks}
                                  title={resolvedRow.workflowStatus === 'REJECTED' && resolvedRow.rejectionReason ? 'Rejection Reason' : 'Office Remarks'}
                                  subtitle={resolvedRow.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td>{renderStatusBadge(resolvedRow.workflowStatus)}</td>
                              <td>{renderActions(resolvedRow)}</td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

              <div className='d-flex justify-content-between align-items-center py-4 px-2 border-top'>
                <div className='d-flex align-items-center gap-3'>
                  <span className='text-muted mb-0'>Rows per page</span>
                  <select
                    className='form-select form-select-sm rounded-pill'
                    style={{ width: 92 }}
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className='d-flex align-items-center gap-3'>
                  <span className='text-muted small'>
                    Showing <strong>{sortedTrainings.length ? indexOfFirst + 1 : 0}-{Math.min(indexOfLast, sortedTrainings.length)}</strong> of <strong>{sortedTrainings.length}</strong>
                  </span>
                  <div className='d-flex align-items-center gap-2'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      {'<'}
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled
                    >
                      {Math.max(1, currentPage)}
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled={currentPage >= Math.max(1, totalPages)}
                      onClick={() => setCurrentPage((p) => Math.min(Math.max(1, totalPages), p + 1))}
                    >
                      {'>'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CrewTrainingModal
      visible={isModalOpen}
      training={editingTraining}
      isNew={isNewTraining}
      lockCrewSelection={isCrew && !isCrewMaster}
      isSuperAdmin={isSuperAdmin}
      isSaving={isSaving}
      crewMembers={selectableCrewMembers}
      ranks={ranks}
      companyGroups={companyAdmins}
      subCompanies={companies}
      vessels={effectiveVessels}
      onClose={() => { setIsModalOpen(false); setEditingTraining(null); setIsNewTraining(false) }}
      onChange={setEditingTraining}
      onSave={handleSaveTraining}
      onUploadExistingAttachment={handleUploadExistingAttachment}
      onAttachmentRemoved={(next) => { setEditingTraining(next); fetchTrainings() }}
    />

    <ViewCrewTrainingModal
      visible={isViewModalOpen}
      training={viewTraining}
      onClose={() => { setIsViewModalOpen(false); setViewTraining(null) }}
    />

    <CrewTrainingAttachmentsModal
      visible={attachmentsModal.visible}
      training={attachmentsModal.training}
      onClose={() => setAttachmentsModal({ visible: false, training: null })}
      onViewAttachment={({ title, fileName, viewUrl, downloadUrl }) => setFileModal({ visible: true, title, fileName, viewUrl, downloadUrl })}
    />

    <CrewTrainingReviewModal
      visible={isReviewModalOpen}
      training={reviewingTraining}
      isLoading={isReviewSaving}
      onClose={() => { setIsReviewModalOpen(false); setReviewingTraining(null) }}
      onSaveRemarks={handleSaveReviewOnly}
      onReview={handleMarkReviewed}
      onReject={handleReject}
    />

    <FileViewerModal
      visible={fileModal.visible}
      title={fileModal.title}
      fileName={fileModal.fileName}
      viewUrl={fileModal.viewUrl}
      downloadUrl={fileModal.downloadUrl}
      onClose={() => setFileModal((prev) => ({ ...prev, visible: false }))}
    />

    <RemarksModal
      visible={remarksModal.visible}
      title={remarksModal.title}
      subtitle={remarksModal.subtitle}
      remarksText={remarksModal.text}
      onClose={() => setRemarksModal(prev => ({ ...prev, visible: false }))}
    />

    <ToastContainer
      position='top-center'
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      style={{ top: '5rem' }}
    />
  </div>
)
}

export default CrewTrainingPage
